import {
  RiArrowRightLine,
  RiPhoneLine,
  RiMailLine,
  RiUser3Line,
  RiCalendarLine,
} from 'react-icons/ri'
import useEnquiryFormHook from '@/hooks/useEnquiryFormHook'

const SERVICES = [
  'Antenatal Empowerment Experience Package',
  'Birth Healing Debrief Sessions',
  'H.E.R Restoration Postpartum Home Visits',
  'H.E.R Birth Journey Experience',
]

const inputClass =
  'border border-base-200 rounded-lg px-4 py-3 text-base-900 text-[14px] bg-white w-full focus:outline-none focus:ring-2 focus:ring-crimson-600 transition-shadow placeholder:text-base-300'

export const EnquiryFormSection = () => {
  const { form, isSubmitting, submitted, handleChange, handleServiceToggle, handleSubmit } =
    useEnquiryFormHook()

  return (
    <section className="relative bg-base-50 py-20 lg:py-28 overflow-hidden">
      {/* ── Decorative watermark ── */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-display font-bold text-base-900 opacity-[0.03] select-none pointer-events-none leading-none"
        style={{ fontSize: 'clamp(8rem, 22vw, 22rem)', letterSpacing: '-0.04em' }}
      >
        H·E·R
      </div>

      {/* ── Decorative circles ── */}
      <div
        aria-hidden="true"
        className="absolute top-0 right-0 w-96 h-96 rounded-full bg-crimson-50 opacity-70 -translate-y-1/2 translate-x-1/3"
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-gold-50 opacity-60 translate-y-1/2 -translate-x-1/3"
      />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* ── Header ── */}
        <div className="text-center mb-12">
          <h2
            className="font-display font-bold text-base-900 leading-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)' }}
          >
            Unlock Your <em className="text-crimson-600 not-italic italic">Free Call</em>
          </h2>
          <div className="w-12 h-[3px] rounded-full bg-gradient-to-r from-crimson-600 to-gold-500 mx-auto mb-5" />
          <p className="text-base-600 text-[15px] leading-relaxed max-w-md mx-auto">
            Begin your journey by sharing a few details and Simone will be in touch.
          </p>
        </div>

        {/* ── Form card ── */}
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-[0_8px_32px_rgba(155,28,28,0.1)] border border-base-100 overflow-hidden">
          <div className="h-[3px] bg-gradient-to-r from-crimson-600 to-gold-500" />

          {submitted ? (
            <div className="p-10 text-center space-y-4">
              <div className="w-14 h-14 rounded-full bg-crimson-50 flex items-center justify-center mx-auto">
                <RiArrowRightLine size={24} className="text-crimson-600" />
              </div>
              <h3 className="font-display font-bold text-base-900 text-2xl">Thank you!</h3>
              <p className="text-base-500 text-[15px] leading-relaxed">
                Your enquiry has been received. Simone will be in touch with you shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              {/* Row: Full Name + Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold uppercase tracking-[0.16em] text-base-500">
                    Full Name
                  </label>
                  <div className="relative">
                    <RiUser3Line
                      size={15}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-300 pointer-events-none"
                    />
                    <input
                      type="text"
                      name="fullName"
                      value={form.fullName}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      className={`${inputClass} pl-9`}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold uppercase tracking-[0.16em] text-base-500">
                    Email Address
                  </label>
                  <div className="relative">
                    <RiMailLine
                      size={15}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-300 pointer-events-none"
                    />
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      required
                      className={`${inputClass} pl-9`}
                    />
                  </div>
                </div>
              </div>

              {/* Row: Phone + Due Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold uppercase tracking-[0.16em] text-base-500">
                    Phone Number
                  </label>
                  <div className="relative">
                    <RiPhoneLine
                      size={15}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-300 pointer-events-none"
                    />
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+44 7000 000000"
                      className={`${inputClass} pl-9`}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold uppercase tracking-[0.16em] text-base-500">
                    Estimated Due Date
                  </label>
                  <div className="relative">
                    <RiCalendarLine
                      size={15}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-300 pointer-events-none"
                    />
                    <input
                      type="date"
                      name="dueDate"
                      value={form.dueDate}
                      onChange={handleChange}
                      className={`${inputClass} pl-9`}
                    />
                  </div>
                </div>
              </div>

              {/* Services checkboxes */}
              <div className="space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-base-500">
                  I'm interested in
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {SERVICES.map((service) => {
                    const checked = form.services.includes(service)
                    return (
                      <label
                        key={service}
                        className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors duration-150 ${
                          checked
                            ? 'bg-crimson-50 border-crimson-200'
                            : 'bg-base-50 border-base-100 hover:border-base-200'
                        }`}
                      >
                        <span
                          className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center shrink-0 border transition-colors duration-150 ${
                            checked
                              ? 'bg-crimson-600 border-crimson-600'
                              : 'bg-white border-base-200'
                          }`}
                        >
                          {checked && (
                            <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                              <path
                                d="M1 3.5L3.5 6L8 1"
                                stroke="white"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </span>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={checked}
                          onChange={() => handleServiceToggle(service)}
                        />
                        <span className="text-[13px] text-base-700 leading-snug">{service}</span>
                      </label>
                    )
                  })}
                </div>
              </div>

              {/* Additional notes */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-[0.16em] text-base-500">
                  Additional Notes{' '}
                  <span className="text-base-300 normal-case tracking-normal">(optional)</span>
                </label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Anything you'd like Simone to know before your call..."
                  rows={4}
                  className={`${inputClass} resize-none`}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full justify-center text-base disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending…' : 'Submit Enquiry'}
                {!isSubmitting && <RiArrowRightLine size={18} />}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  )
}
